/**
 * Post Share Modal
 * WriteFlow's share is a Wordle-style artifact, not an ad: a scorecard line
 * ("WriteFlow #6 · 412 words"), the day's prompt in quotes, and the opening of
 * the writing cut mid-sentence — no link. The day number is searchable and
 * /day/:n owns it, so curiosity does the linking.
 *
 * Text platforms (X / WhatsApp / Reddit / native sheet) get the artifact.
 * Facebook only shares URLs, so it keeps the post link (whose og:image is the
 * server-rendered prompt card). Copy-link stays for people who want the URL.
 *
 * The photo: share-intent URLs can't attach images (no platform allows it), so
 * the rendered card travels two other ways — the native sheet shares the JPEG
 * file alongside the text where supported, and "Save card" downloads it for
 * manual attach on desktop Reddit/X.
 */

import React, { useState, useEffect } from 'react';
import { X, Copy, Check, Share2, Download } from 'lucide-react';
import { capture } from '../../utils/analytics';
import { formatPromptDate } from '../../utils/formatPromptDate';
import { dateToDayNumber } from '../../utils/promptDayNumber';
import './PostShareModal.css';

const EXCERPT_LEN = 200;
const EXCERPT_LEN_X = 110; // keep the whole artifact under X's 280

/**
 * Opening of the writing, cut on a word boundary. The artifact version has NO
 * ellipsis — a hard mid-sentence stop reads like a cliffhanger, not a teaser.
 */
function excerpt(text = '', len = EXCERPT_LEN, ellipsis = true) {
  const clean = text.replace(/\s+/g, ' ').trim();
  if (clean.length <= len) return clean;
  const cut = clean.slice(0, len);
  const lastSpace = cut.lastIndexOf(' ');
  const hard = (lastSpace > len * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd();
  return ellipsis ? hard + '…' : hard;
}

function countWords(text = '') {
  return text.trim().split(/\s+/).filter(Boolean).length;
}

const enc = encodeURIComponent;

const PostShareModal = ({ post, onClose }) => {
  const [copied, setCopied] = useState(false);
  const prompt = post.prompt;
  const url = `${window.location.origin}/post/${post._id}`;
  const previewBody = excerpt(post.content);
  const promptLine = prompt
    ? [prompt.emotion, prompt.connector, prompt.setting].filter(Boolean).join(' ')
    : '';
  const dayNumber = prompt?.date ? dateToDayNumber(prompt.date) : null;
  const brand = dayNumber ? `WriteFlow #${dayNumber}` : 'WriteFlow';
  const canNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  // The linkless share artifact. Scorecard · prompt · hard-cut opening.
  // ponytail: no streak in the scorecard yet — it's only computed client-side
  // for the logged-in viewer, not post authors; add "🔥N" when authors carry one.
  const buildArtifact = (excerptLen) => {
    const scorecard = `${brand} · ${countWords(post.content)} words`;
    const quote = excerpt(post.content, excerptLen, false);
    return (
      scorecard +
      (promptLine ? `\n"${promptLine}"` : '') +
      (quote ? `\n\n> ${quote}` : '')
    );
  };

  const artifact = buildArtifact(EXCERPT_LEN);
  const redditTitle = promptLine ? `${brand} — "${promptLine}"` : brand;
  // Server-rendered card (photo + prompt text burned in); prod-only route.
  const cardUrl = prompt?.imageUrl && prompt?._id ? `/prompt/${prompt._id}/card.jpg` : null;
  const cardFileName = `writeflow-${dayNumber || 'prompt'}.jpg`;

  // Brand share targets. Inline SVGs (not an icon lib) so this never breaks on an
  // icon-package version bump. Each opens the platform's prefilled share dialog.
  const platforms = [
    {
      name: 'X',
      cls: 'sh-x',
      path: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
      href: `https://twitter.com/intent/tweet?text=${enc(buildArtifact(EXCERPT_LEN_X))}`,
    },
    {
      name: 'Facebook',
      cls: 'sh-fb',
      path: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
      href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
    },
    {
      name: 'WhatsApp',
      cls: 'sh-wa',
      path: 'M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413z',
      href: `https://wa.me/?text=${enc(artifact)}`,
    },
    {
      name: 'Reddit',
      cls: 'sh-rd',
      path: 'M24 11.779c0-1.459-1.192-2.645-2.657-2.645-.715 0-1.363.286-1.84.746-1.81-1.191-4.259-1.949-6.971-2.046l1.483-4.669 4.016.941-.006.058c0 1.193.975 2.163 2.174 2.163 1.198 0 2.172-.97 2.172-2.163s-.974-2.164-2.172-2.164c-.92 0-1.704.574-2.021 1.379l-4.493-1.052c-.146-.034-.293.05-.335.195l-1.647 5.189c-2.758.076-5.25.836-7.087 2.033-.476-.45-1.117-.729-1.821-.729-1.465 0-2.657 1.186-2.657 2.645 0 1.026.594 1.906 1.456 2.336-.039.232-.06.47-.06.71 0 3.617 4.213 6.557 9.405 6.557s9.405-2.94 9.405-6.557c0-.24-.022-.479-.061-.711.863-.43 1.458-1.31 1.458-2.336zm-17.247 1.844c0-.795.647-1.442 1.442-1.442.795 0 1.442.647 1.442 1.442 0 .794-.647 1.442-1.442 1.442-.795 0-1.442-.648-1.442-1.442zm8.665 4.245c-1.04 1.04-3.03 1.117-3.615 1.117-.586 0-2.577-.077-3.615-1.117-.154-.154-.154-.404 0-.558.154-.153.404-.153.558 0 .652.652 2.046.881 3.057.881 1.011 0 2.405-.229 3.058-.881.154-.153.403-.153.557 0 .154.154.154.404 0 .558zm-.811-2.803c-.795 0-1.442-.648-1.442-1.442 0-.795.647-1.442 1.442-1.442.795 0 1.442.647 1.442 1.442 0 .794-.647 1.442-1.442 1.442z',
      // Self/text post: the artifact IS the body; no link.
      href: `https://www.reddit.com/submit?type=TEXT&title=${enc(redditTitle)}&text=${enc(artifact)}`,
    },
  ];

  useEffect(() => {
    capture('share_opened');
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const copyLink = async () => {
    capture('share_clicked', { method: 'copy_link' });
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      window.prompt('Copy this link:', url); // clipboard blocked (insecure context)
    }
  };

  // Native sheet: attach the card JPEG when the platform can take files
  // (Android/iOS pass it to WhatsApp/Instagram/iMessage); else text-only.
  // One navigator.share call either way — a fallback after a user-cancelled
  // file share would pop a second sheet.
  const nativeShare = async () => {
    capture('share_clicked', { method: 'native' });
    let payload = { text: artifact };
    try {
      if (cardUrl && navigator.canShare) {
        const blob = await (await fetch(cardUrl)).blob();
        const file = new File([blob], cardFileName, { type: 'image/jpeg' });
        if (navigator.canShare({ files: [file] })) payload = { text: artifact, files: [file] };
      }
    } catch {} // card unavailable — share the text alone
    navigator.share(payload).catch(() => {});
  };

  return (
    <div className="post-share-backdrop" onClick={onClose}>
      <div
        className="post-share-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Share this post"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="post-share-close" onClick={onClose} aria-label="Close">
          <X size={20} />
        </button>

        <h2 className="post-share-title">Share this post</h2>

        <div className="post-share-card">
          {prompt && (
            <>
              <div className="post-share-cardhead">
                <span className="post-share-cardbadge">{dayNumber ? brand : 'Response to'}</span>
                {prompt.date && <span className="post-share-carddate">{formatPromptDate(prompt.date)}</span>}
              </div>
              <div
                className="post-share-prompt"
                style={prompt.imageUrl ? { backgroundImage: `url(${prompt.imageUrl})` } : undefined}
              >
                <div className="post-share-prompt-overlay">
                  <p className="post-share-emotion">{prompt.emotion}</p>
                  <p className="post-share-connector">{prompt.connector}</p>
                  <p className="post-share-setting">{prompt.setting}</p>
                </div>
              </div>
            </>
          )}
          <p className="post-share-excerpt">{previewBody}</p>
        </div>

        <p className="post-share-label">Post it to</p>
        <div className="post-share-platforms">
          {platforms.map((p) => (
            <button
              key={p.name}
              type="button"
              className={`post-share-platform ${p.cls}`}
              onClick={() => {
                capture('share_clicked', { method: p.name.toLowerCase() });
                window.open(p.href, '_blank', 'noopener,noreferrer');
              }}
              title={`Share on ${p.name}`}
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true">
                <path d={p.path} />
              </svg>
              <span>{p.name}</span>
            </button>
          ))}
        </div>

        <div className="post-share-actions">
          <button className="post-share-secondary" onClick={copyLink}>
            {copied ? <Check size={16} /> : <Copy size={16} />}
            {copied ? 'Copied!' : 'Copy link'}
          </button>
          {cardUrl && (
            <a
              className="post-share-secondary"
              href={cardUrl}
              download={cardFileName}
              onClick={() => capture('share_clicked', { method: 'save_card' })}
            >
              <Download size={16} /> Save card
            </a>
          )}
          {canNativeShare && (
            <button className="post-share-secondary" onClick={nativeShare}>
              <Share2 size={16} /> More…
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PostShareModal;
